import React from 'react';
import { motion } from 'framer-motion';
import { useCustomizationStore } from '../store/customizationStore';
import { useLocalizationStore } from '../store/localizationStore';
import { formatCurrency, calculateArea, calculateMaterialCost } from '../utils/units';
import { premiumServices, edgeProfiles } from '../data/customization';
import { 
  Receipt,
  Star, 
  Shield, 
  Clock, 
  MessageCircle, 
  X, 
  Info, 
  ArrowRight, 
  SunMoon, 
  Check,
  Box as Box3D,
  Grid,
  Eye,
  Calendar,
  RotateCcw,
  ZoomIn,
  ZoomOut,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Ruler,
  Palette,
  Settings2,
  PenTool,
  Truck,
  Wrench,
  Heart,
  Sparkles,
  Maximize2,
  Minimize2,
  Move3d,
  Layers,
  Zap,
  AlertCircle,
  Mail,
  Phone
} from 'lucide-react';

interface OrderSummaryProps {
  onContinue?: () => void;
  showActions?: boolean;
  className?: string;
  options?: any;
  material?: any;
  selectedServices?: string[];
  currency?: string;
  language?: string;
}

const VAT_RATE = 0.21; // 21% VAT

export const OrderSummary: React.FC<OrderSummaryProps> = ({ 
  onContinue, 
  showActions = true,
  className = '',
  options,
  material,
  selectedServices: propSelectedServices,
  currency = 'EUR',
  language = 'en-US'
}) => {
  const { 
    customizationOptions, 
    selectedMaterial, 
    customizationPath,
    selectedMaterials 
  } = useCustomizationStore();

  // Use props if provided, otherwise use store values
  const effectiveOptions = options || customizationOptions;
  const effectiveMaterial = material || selectedMaterial;
  const effectiveSelectedServices = propSelectedServices || effectiveOptions?.selectedServices || [];

  if (!effectiveOptions && !selectedMaterials.length) return null;

  // Calculate perimeter for edge pricing
  const calculatePerimeter = (width: number, length: number, shape: string = 'rectangular'): number => {
    // Convert mm to meters
    const w = width / 1000;
    const l = length / 1000;
    
    switch(shape) {
      case 'round':
        // Circumference of a circle = 2πr = πd
        return Math.PI * w;
      case 'oval':
        // Approximation for oval perimeter
        return 2 * Math.PI * Math.sqrt((w*w + l*l) / 8);
      case 'racetrack':
        // Two straight sides + two semicircles
        const straightLength = l - w;
        return 2 * straightLength + Math.PI * w;
      default:
        // Rectangle perimeter
        return 2 * (w + l);
    }
  };

  // Find the selected edge profile
  const getSelectedEdgeProfile = () => {
    if (!effectiveOptions?.edgeProfile) return null;
    
    for (const category of edgeProfiles) {
      const profile = category.profiles.find(p => p.id === effectiveOptions.edgeProfile);
      if (profile) return profile;
    }
    return null;
  };

  const calculateTotals = () => {
    if (customizationPath === 'slab-only' && selectedMaterials.length > 0) {
      // Calculate total for slab-only orders
      const materialTotal = selectedMaterials.reduce((total, item) => {
        // Calculate cost based on number of slabs needed
        const materialCost = calculateMaterialCost(item.area, item.material.price);
        return total + materialCost;
      }, 0);

      const servicesTotal = effectiveSelectedServices.reduce((total, serviceId) => {
        const service = premiumServices.find(s => s.id === serviceId);
        return total + (service?.price || 0);
      }, 0);

      const subtotal = materialTotal + servicesTotal;
      const vatAmount = subtotal * VAT_RATE;

      return {
        basePrice: materialTotal,
        servicesTotal,
        vatAmount,
        total: subtotal + vatAmount
      };
    } else if (effectiveOptions && effectiveMaterial) {
      // Calculate total for custom orders
      const area = calculateArea(effectiveOptions.width, effectiveOptions.length);
      // Calculate cost based on number of slabs needed
      const materialCost = calculateMaterialCost(area, effectiveMaterial.price);
      
      // Calculate edge profile cost
      const perimeter = calculatePerimeter(
        effectiveOptions.width, 
        effectiveOptions.length, 
        effectiveOptions.shape
      );
      
      const selectedEdgeProfile = getSelectedEdgeProfile();
      const edgeCost = selectedEdgeProfile && selectedEdgeProfile.price ? 
        selectedEdgeProfile.price * perimeter : 0;

      const servicesTotal = effectiveSelectedServices.reduce((total, serviceId) => {
        const service = premiumServices.find(s => s.id === serviceId);
        return total + (service?.price || 0);
      }, 0);

      const subtotal = materialCost + edgeCost + servicesTotal;
      const vatAmount = subtotal * VAT_RATE;

      return {
        basePrice: materialCost,
        edgeCost,
        servicesTotal,
        vatAmount,
        total: subtotal + vatAmount
      };
    }

    return {
      basePrice: 0,
      edgeCost: 0,
      servicesTotal: 0,
      vatAmount: 0,
      total: 0
    };
  };

  const calculateWeight = () => {
    if (customizationPath === 'slab-only' && selectedMaterials.length > 0) {
      return selectedMaterials.reduce((total, item) => {
        // Weight per m² based on thickness
        const weightPerM2 = item.material.thickness === 20 ? 46.875 : 28.125; // kg/m²
        return total + (item.area * weightPerM2);
      }, 0);
    } else if (effectiveOptions && effectiveMaterial) {
      const area = calculateArea(effectiveOptions.width, effectiveOptions.length);
      // Weight per m² based on thickness
      const weightPerM2 = effectiveOptions.thickness === 20 ? 46.875 : 28.125; // kg/m²
      return area * weightPerM2;
    }
    return 0;
  };

  const totals = calculateTotals();
  const totalWeight = calculateWeight();

  return (
    <div className={`bg-white rounded-xl shadow-lg overflow-hidden ${className}`}>
      {/* Header */}
      <div className="p-6 bg-gradient-to-br from-gold to-gold-dark text-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Receipt className="w-6 h-6" />
            <h3 className="text-xl font-semibold">Order Summary</h3>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-white/80 text-sm">Total Amount (incl. VAT)</p>
            <p className="text-2xl font-bold mt-1">
              {formatCurrency(totals.total, currency, language)}
            </p>
          </div>
          <div className="text-right">
            <div className="flex items-center justify-end gap-1 text-white/80 text-sm">
              <Clock className="w-4 h-4" />
              <span>Est. Delivery</span>
            </div>
            <p className="font-medium mt-1">
              {new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toLocaleDateString(language, { 
                month: 'short', 
                day: 'numeric' 
              })}
            </p>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="mt-4 grid grid-cols-4 gap-2">
          <div className="text-center">
            <Star className="w-4 h-4 mx-auto mb-1" />
            <p className="text-sm">98%</p>
            <p className="text-xs text-white/80">Rating</p>
          </div>
          <div className="text-center">
            <Shield className="w-4 h-4 mx-auto mb-1" />
            <p className="text-sm">10 Years</p>
            <p className="text-xs text-white/80">Warranty</p>
          </div>
          <div className="text-center">
            <Truck className="w-4 h-4 mx-auto mb-1" />
            <p className="text-sm">Free</p>
            <p className="text-xs text-white/80">Delivery</p>
          </div>
          <div className="text-center">
            <Heart className="w-4 h-4 mx-auto mb-1" />
            <p className="text-sm">Premium</p>
            <p className="text-xs text-white/80">Quality</p>
          </div>
        </div>
      </div>

      {/* Order Details */}
      <div className="p-6 space-y-4">
        {customizationPath === 'slab-only' ? (
          <div>
            <h4 className="text-sm font-medium text-black/70 mb-3">Selected Materials</h4>
            {selectedMaterials.map((item, index) => (
              <div key={index} className="bg-cream rounded-lg p-4 mb-4">
                <div className="flex items-start gap-4 mb-4">
                  <img
                    src={item.material.image}
                    alt={item.material.name}
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-gold-dark">{item.material.name}</p>
                    <p className="text-sm text-black/60">{item.material.description}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-black/60">Required Area</p>
                    <p className="font-medium text-gold-dark">{item.area.toFixed(2)} m²</p>
                  </div>
                  <div>
                    <p className="text-sm text-black/60">Weight</p>
                    <p className="font-medium text-gold-dark">
                      {(item.area * (item.material.thickness === 20 ? 46.875 : 28.125)).toFixed(1)} kg
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-black/60">Material Cost</p>
                    <p className="font-medium text-gold-dark">
                      {formatCurrency(calculateMaterialCost(item.area, item.material.price), currency, language)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-black/60">Finish</p>
                    <p className="font-medium text-gold-dark capitalize">
                      {item.material.selectedFinish || 'Glossy'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          effectiveMaterial && effectiveOptions && (
            <div>
              <h4 className="text-sm font-medium text-black/70 mb-3">Material</h4>
              <div className="bg-cream rounded-lg p-4">
                <div className="flex items-start gap-4 mb-4">
                  <img
                    src={effectiveMaterial.image}
                    alt={effectiveMaterial.name}
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-gold-dark">{effectiveMaterial.name}</p>
                    <p className="text-sm text-black/60">{effectiveMaterial.description}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-black/60">Dimensions</p>
                    <p className="font-medium text-gold-dark">
                      {effectiveOptions.width}×{effectiveOptions.length} mm
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-black/60">Thickness</p>
                    <p className="font-medium text-gold-dark">{effectiveOptions.thickness} mm</p>
                  </div>
                  <div>
                    <p className="text-sm text-black/60">Shape</p>
                    <p className="font-medium text-gold-dark capitalize">
                      {effectiveOptions.shape || 'Rectangular'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-black/60">Edge Profile</p>
                    <p className="font-medium text-gold-dark capitalize">
                      {effectiveOptions.edgeProfile}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-black/60">Weight</p>
                    <p className="font-medium text-gold-dark">
                      {totalWeight.toFixed(1)} kg
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-black/60">Finish</p>
                    <p className="font-medium text-gold-dark capitalize">
                      {effectiveMaterial.selectedFinish || 'Glossy'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )
        )}

        {/* Selected Premium Services */}
        {effectiveSelectedServices.length > 0 && (
          <div className="pt-4 border-t border-gold/10">
            <h4 className="text-sm font-medium text-black/70 mb-3">Selected Services</h4>
            <div className="space-y-3">
              {effectiveSelectedServices.map(serviceId => {
                const service = premiumServices.find(s => s.id === serviceId);
                if (!service) return null;
                
                const ServiceIcon = service.icon;
                return (
                  <div key={serviceId} className="bg-cream rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gold/10 rounded-lg">
                        <ServiceIcon className="w-5 h-5 text-gold" />
                      </div>
                      <div>
                        <p className="font-medium text-gold-dark">{service.name}</p>
                        <p className="text-sm text-black/60">{service.description}</p>
                      </div>
                    </div>
                    <p className="font-medium text-gold-dark">
                      {formatCurrency(service.price, currency, language)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Price Breakdown */}
        <div className="pt-4 border-t border-gold/10 space-y-2">
          <div className="flex justify-between text-black/70">
            <span>Material Cost</span>
            <span>{formatCurrency(totals.basePrice, currency, language)}</span>
          </div>
          
          {/* Edge Profile Cost - Only show if there's a cost */}
          {'edgeCost' in totals && totals.edgeCost > 0 && (
            <div className="flex justify-between text-black/70">
              <span>Edge Profile</span>
              <span>{formatCurrency(totals.edgeCost, currency, language)}</span>
            </div>
          )}
          
          {totals.servicesTotal > 0 && (
            <div className="flex justify-between text-black/70">
              <span>Premium Services</span>
              <span>{formatCurrency(totals.servicesTotal, currency, language)}</span>
            </div>
          )}
          
          <div className="flex justify-between text-black/70">
            <span>Subtotal</span>
            <span>{formatCurrency(totals.basePrice + (totals.edgeCost || 0) + totals.servicesTotal, currency, language)}</span>
          </div>
          
          <div className="flex justify-between text-black/70">
            <span>VAT (21%)</span>
            <span>{formatCurrency(totals.vatAmount, currency, language)}</span>
          </div>
          
          <div className="pt-2 border-t border-gold/10 flex justify-between text-xl font-bold text-gold">
            <span>Total</span>
            <span>{formatCurrency(totals.total, currency, language)}</span>
          </div>
        </div>

        {/* Actions */}
        {showActions && onContinue && (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onContinue}
            className="w-full mt-6 bg-gold text-white py-4 px-6 rounded-xl font-semibold text-lg flex items-center justify-center hover:bg-gold-dark transition-colors"
          >
            Continue to Quote
            <ArrowRight className="w-5 h-5 ml-2" />
          </motion.button>
        )}
      </div>
    </div>
  );
};